import { useEffect, useState } from "react";
import LocalizedLink from "@/components/LocalizedLink";
import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { useSubscriptionStore, useAuthStore } from "@/store";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import { translateCurrentPathText as t } from "@/lib/translations";
import {
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  getPathLanguage,
  stripLanguagePrefix,
  withLanguagePrefix,
} from "@/lib/i18n";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Check, X, Sparkles, Zap, Crown, Menu, CheckCircle2 } from "lucide-react";
import { useLocation } from "react-router-dom";

const Navbar = () => {
  const { isAuthenticated, user } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <LocalizedLink to="/" className="flex items-center gap-2">
            <img src="/assets/logo-creavideos.png" alt="CreaVideos" className="h-8 w-auto" />
          </LocalizedLink>

          <div className="hidden md:flex items-center gap-8">
            <LocalizedLink to="/templates" className="text-slate-600 hover:text-teal-600 transition-colors font-medium">Templates</LocalizedLink>
            <LocalizedLink to="/pricing" className="text-teal-600 font-medium">Pricing</LocalizedLink>
            <LanguageSelector />
            {isAuthenticated ? (
              <LocalizedLink to={user?.role === "admin" ? "/admin" : "/dashboard"}>
                <Button className="btn-primary rounded-full px-6">Dashboard</Button>
              </LocalizedLink>
            ) : (
              <div className="flex items-center gap-3">
                <LocalizedLink to="/login"><Button variant="ghost" className="btn-ghost">Log in</Button></LocalizedLink>
              </div>
            )}
          </div>

          <div className="flex items-center md:hidden">
            <LanguageSelector />
            <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle navigation menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <LocalizedLink to="/templates" className="text-slate-600 font-medium py-2">
                Templates
              </LocalizedLink>
              <LocalizedLink to="/pricing" className="text-teal-600 font-medium py-2">
                Pricing
              </LocalizedLink>
              {isAuthenticated ? (
                <LocalizedLink to={user?.role === "admin" ? "/admin" : "/dashboard"}>
                  <Button className="btn-primary rounded-full w-full">Dashboard</Button>
                </LocalizedLink>
              ) : (
                <LocalizedLink to="/login">
                  <Button variant="outline" className="w-full">Log in</Button>
                </LocalizedLink>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const planIcons = {
  starter: Sparkles,
  growth: Zap,
  pro: Crown,
  agency: Crown,
};

export default function PricingPage() {
  const location = useLocation();
  const {
    plans,
    extraVideoPacks,
    fetchPlans,
    createSubscription,
    captureSubscription,
    createExtraVideoOrder,
    captureExtraVideoOrder,
    validateVat,
    paypalClientId,
    fetchPayPalConfig,
    isLoading,
  } = useSubscriptionStore();
  const { isAuthenticated, user, checkAuth, register, updateProfile } = useAuthStore();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showPayPal, setShowPayPal] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [vatApplies, setVatApplies] = useState(true);
  const [vatValidated, setVatValidated] = useState(false);
  const [vatInvalid, setVatInvalid] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    country: "",
    company_name: "",
    vat_number: "",
    address: "",
    zip_code: "",
    city: "",
    phone: "",
  });
  const [selectedPack, setSelectedPack] = useState(null);
  const [showExtraPackPayPal, setShowExtraPackPayPal] = useState(false);
  const [extraPackVatApplies, setExtraPackVatApplies] = useState(true);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const currentLanguage = getPathLanguage(location.pathname);

  const shouldApplyVatForUser = (currentUser) => {
    const vatNumber = String(currentUser?.vat_number || "").trim();
    const vatValid = !!currentUser?.vat_valid;
    const cleanedVat = vatNumber.replace(/[^a-zA-Z0-9]/g, "");
    const countryCode = cleanedVat.slice(0, 2).toUpperCase();
    if (!vatValid || !countryCode) return true;
    return countryCode === "ES";
  };

  useEffect(() => {
    fetchPlans();
    fetchPayPalConfig();
  }, [fetchPlans, fetchPayPalConfig]);

  useEffect(() => {
    const hasOpenModal = showCheckoutModal || showExtraPackPayPal;
    if (!hasOpenModal) return undefined;

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyTouchAction = body.style.touchAction;
    const previousHtmlOverflow = documentElement.style.overflow;

    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    documentElement.style.overflow = "hidden";

    return () => {
      body.style.overflow = previousBodyOverflow;
      body.style.touchAction = previousBodyTouchAction;
      documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [showCheckoutModal, showExtraPackPayPal]);

  const navigate = useLocalizedNavigation();
  const mainPlans = plans;
  const userVatApplies = shouldApplyVatForUser(user);
  const getPlanCycleLabel = (plan) => (String(plan?.billing_interval_unit || "").toUpperCase() === "DAY" ? t("/day") : t("/month"));
  const getRenewalLabel = (plan) => (String(plan?.billing_interval_unit || "").toUpperCase() === "DAY" ? t("Then daily") : t("Then monthly"));
  const formatEuro = (amount) => `${amount}€`;

  const switchLanguage = (nextLanguage) => {
    const basePath = stripLanguagePrefix(location.pathname);
    const localizedPath = withLanguagePrefix(basePath, nextLanguage);
    window.location.assign(`${localizedPath}${location.search}${location.hash}`);
    setLanguageMenuOpen(false);
  };

  const handleSelectPlan = (plan) => {
    const existingName = String(user?.name || "").trim();
    const fallbackFirstName = existingName ? existingName.split(/\s+/)[0] : "";
    const fallbackLastName = existingName.includes(" ")
      ? existingName.split(/\s+/).slice(1).join(" ")
      : "";

    setSelectedPlan(plan);
    setShowCheckoutModal(true);
    setShowPayPal(false);
    setVatApplies(true);
    setVatValidated(false);
    setVatInvalid(false);
    setFormData((prev) => ({
      ...prev,
      first_name: user?.first_name || fallbackFirstName || prev.first_name,
      last_name: user?.last_name || fallbackLastName || prev.last_name,
      email: user?.email || prev.email,
      country: user?.country || prev.country,
      company_name: user?.company_name || prev.company_name,
      vat_number: user?.vat_number || prev.vat_number,
      address: user?.address || prev.address,
      zip_code: user?.zip_code || prev.zip_code,
      city: user?.city || prev.city,
      phone: user?.phone || prev.phone,
    }));
  };

  const handlePayPalApprove = async (data) => {
    const result = await captureSubscription(data.subscriptionID);
    if (result) {
      toast.success(t("Subscription activated successfully!"));
      await checkAuth();
      setShowPayPal(false);
      setShowCheckoutModal(false);
      setSelectedPlan(null);
      navigate("/dashboard");
    } else {
      toast.error(t("Payment failed. Please try again."));
    }
  };

  const handlePayPalCreateSubscription = async () => {
    if (!selectedPlan) return "";
    const result = await createSubscription(selectedPlan.id, { vat_applies: vatApplies });
    if (result?.subscription_id) {
      return result.subscription_id;
    }
    throw new Error("Failed to create subscription");
  };

  const handleSaveAndSubscribe = async () => {
    if (!selectedPlan) return;
    const fullName = `${String(formData.first_name || "").trim()} ${String(formData.last_name || "").trim()}`.trim();
    const requiredFields = [
      formData.first_name,
      formData.last_name,
      formData.email,
      formData.country,
      formData.company_name,
      formData.address,
      formData.zip_code,
      formData.city,
      formData.phone,
    ];

    if (requiredFields.some((value) => !String(value || "").trim()) || (!isAuthenticated && !formData.password)) {
      toast.error(t("Please fill all required fields. VAT is optional."));
      return;
    }

    setSavingProfile(true);
    let vatValid = false;
    let vatCountryCode = "";
    if (formData.vat_number?.trim()) {
      const vatResult = await validateVat(formData.vat_number.trim());
      vatValid = !!vatResult?.valid;
      vatCountryCode = formData.vat_number.trim().replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase();
      setVatInvalid(!vatValid && vatResult?.error !== "vies_unreachable");
      if (!vatValid && vatResult?.error === "vies_unreachable") {
        toast.error(t("VAT check service is temporarily unavailable. VAT was applied by default."));
      }
    } else {
      setVatInvalid(false);
    }
    const shouldApplyVat = !vatValid || vatCountryCode === "ES";
    setVatValidated(vatValid);
    setVatApplies(shouldApplyVat);

    const payload = { ...formData, name: fullName, vat_valid: vatValid };
    if (isAuthenticated) {
      const result = await updateProfile(payload);
      if (!result.success) {
        toast.error(result.error || t("Unable to save your information."));
        setSavingProfile(false);
        return;
      }
    } else {
      const result = await register(payload);
      if (!result.success) {
        toast.error(result.error || t("Unable to create account."));
        setSavingProfile(false);
        return;
      }
    }

    await checkAuth();
    let resolvedClientId = paypalClientId;
    if (!resolvedClientId) {
      const paypalConfig = await fetchPayPalConfig();
      resolvedClientId = paypalConfig?.client_id || "";
    }
    if (!resolvedClientId) {
      toast.error(t("PayPal is not configured yet. Please try again in a few minutes."));
      setSavingProfile(false);
      return;
    }
    setShowPayPal(true);
    setSavingProfile(false);
  };

  const handleBuyExtra = async (pack) => {
    if (!isAuthenticated) {
      navigate("/login", { state: { redirect: "/pricing" } });
      return;
    }

    setSelectedPack(pack);
    setExtraPackVatApplies(shouldApplyVatForUser(user));
    setShowExtraPackPayPal(true);
  };

  const handlePayPalCreateExtraOrder = async () => {
    if (!selectedPack) return "";
    const result = await createExtraVideoOrder(selectedPack.id);
    if (result?.order_id) {
      if (typeof result.vat_applies === "boolean") {
        setExtraPackVatApplies(result.vat_applies);
      }
      return result.order_id;
    }
    throw new Error("Failed to create extra videos order");
  };

  const handlePayPalApproveExtraOrder = async (data) => {
    const result = await captureExtraVideoOrder(data.orderID);
    if (result) {
      toast.success(`Extra videos added: +${result.added_videos}`);
      await checkAuth();
      setShowExtraPackPayPal(false);
      setSelectedPack(null);
      return;
    }
    toast.error(t("Payment failed. Please try again."));
  };

  const subscriptionPayPalOptions = {
    clientId: paypalClientId,
    currency: "EUR",
    vault: true,
    intent: "subscription",
  };

  const extraPackPayPalOptions = {
    clientId: paypalClientId,
    currency: "EUR",
    intent: "capture",
  };

  const pricingContent = (
    <div className="min-h-screen bg-slate-50">
        <Navbar />

        <main className="pt-24 pb-16 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Simple, transparent pricing</h1>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">Every plan starts with a 50% discounted first month, then renews monthly.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mainPlans.map((plan) => {
                const Icon = planIcons[plan.id] || Sparkles;
                const isCurrentPlan = user?.subscription_plan === plan.id;
                const isPopular = plan.id === "growth";

                return (
                  <div
                    key={plan.id}
                    className={`relative bg-white rounded-2xl border-2 p-6 transition-all ${
                      isPopular ? "border-teal-500 shadow-lg shadow-teal-500/10" : "border-slate-200"
                    } ${isCurrentPlan ? "ring-2 ring-green-500" : ""}`}
                    data-testid={`plan-${plan.id}`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="px-3 py-1 rounded-full bg-teal-600 text-white text-xs font-semibold">Most Popular</span>
                      </div>
                    )}

                    <div className="mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isPopular ? "bg-teal-100" : "bg-slate-100"}`}>
                        <Icon className={`w-6 h-6 ${isPopular ? "text-teal-600" : "text-slate-600"}`} />
                      </div>
                      <h3 className="text-xl font-bold">{t(plan.name)}</h3>
                      <p className="text-sm text-slate-500">{plan.description}</p>
                    </div>

                    <div className="mb-6">
                      <div>
                        <span className="text-3xl font-bold">{formatEuro(plan.price)}</span>
                        <span className="text-slate-500">{getPlanCycleLabel(plan)}</span>
                      </div>
                      <div className="text-sm text-teal-700 font-medium mt-1">
                        {t("Start for")} {formatEuro(plan.first_month_price)} {t("(50% OFF first month)")}
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={`w-full rounded-full ${isPopular ? "btn-primary" : "btn-secondary"}`}
                      onClick={() => handleSelectPlan(plan)}
                      disabled={isCurrentPlan || isLoading}
                      data-testid={`select-plan-${plan.id}`}
                    >
                      {isCurrentPlan ? "Current Plan" : "Subscribe"}
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="text-2xl font-bold mb-2">Extra video packs</h3>
              <p className="text-slate-600 mb-6">Need more exports this month? Add extra videos instantly.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {extraVideoPacks.map((pack) => (
                  <div key={pack.id} className="rounded-xl border border-slate-200 p-4">
                    <p className="font-semibold text-lg">+{pack.videos} videos</p>
                    <p className="text-slate-600 mb-4">{formatEuro(pack.price)}</p>
                    <Button className="w-full rounded-full btn-secondary" onClick={() => handleBuyExtra(pack)}>
                      Buy
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3">21% VAT is added when applicable.</p>
            </div>

            {showCheckoutModal && selectedPlan && (
              <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/50 p-4 sm:p-6">
                <div className="min-h-full flex items-start justify-center py-4 sm:py-8">
                  <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold">Subscribe to {t(selectedPlan.name)}</h3>
                    <button onClick={() => { setShowPayPal(false); setShowCheckoutModal(false); setSelectedPlan(null); }} className="p-2 hover:bg-slate-100 rounded-full">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("First name")} value={formData.first_name} onChange={(e) => setFormData((p) => ({ ...p, first_name: e.target.value }))} />
                      <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("Last name")} value={formData.last_name} onChange={(e) => setFormData((p) => ({ ...p, last_name: e.target.value }))} />
                    </div>
                    <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("Email")} value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} />
                    {!isAuthenticated && (
                      <input type="password" className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("Password")} value={formData.password} onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))} />
                    )}
                    <div className="h-1" />
                    <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("Country")} value={formData.country} onChange={(e) => setFormData((p) => ({ ...p, country: e.target.value }))} />
                    <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("Company name")} value={formData.company_name} onChange={(e) => setFormData((p) => ({ ...p, company_name: e.target.value }))} />
                    <div className="relative">
                      <input className="w-full border rounded-md px-3 py-2 text-sm pr-10" placeholder={t("VAT (optional)")} value={formData.vat_number} onChange={(e) => { setVatValidated(false); setVatInvalid(false); setFormData((p) => ({ ...p, vat_number: e.target.value })); }} />
                      {vatValidated && (
                        <CheckCircle2 className="w-5 h-5 text-green-600 absolute right-3 top-1/2 -translate-y-1/2" />
                      )}
                    </div>
                    <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("Address")} value={formData.address} onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))} />
                    <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("Zip code")} value={formData.zip_code} onChange={(e) => setFormData((p) => ({ ...p, zip_code: e.target.value }))} />
                    <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("City")} value={formData.city} onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))} />
                    <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder={t("Phone")} value={formData.phone} onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))} />
                  </div>

                  <Button className="w-full mb-4 rounded-full" onClick={handleSaveAndSubscribe} disabled={savingProfile}>
                    {savingProfile ? t("Saving...") : t("Save and Subscribe")}
                  </Button>

                  <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-center text-sm">
                      <span>First month</span>
                      <span className="font-semibold">{formatEuro((selectedPlan.first_month_price * (vatApplies ? 1.21 : 1)).toFixed(2))}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span>{getRenewalLabel(selectedPlan)}</span>
                      <span className="font-semibold">{formatEuro((selectedPlan.price * (vatApplies ? 1.21 : 1)).toFixed(2))}</span>
                    </div>
                    {vatValidated && !vatApplies && <p className="text-xs text-green-700 mt-2">Valid VAT number detected. VAT not applied.</p>}
                    {vatInvalid && <p className="text-xs text-red-600 mt-2">VAT number not valid.</p>}
                    {vatValidated && vatApplies && formData.vat_number.trim().toUpperCase().startsWith("ES") && (
                      <p className="text-xs text-slate-500 mt-2">Spanish VAT numbers (ES) still include 21% VAT.</p>
                    )}
                    {!vatValidated && vatApplies && <p className="text-xs text-slate-500 mt-2">21% VAT applied.</p>}
                  </div>

                  {showPayPal && paypalClientId && (
                    <PayPalScriptProvider options={subscriptionPayPalOptions}>
                      <PayPalButtons
                        style={{ layout: "vertical", label: "subscribe" }}
                        createSubscription={handlePayPalCreateSubscription}
                        onApprove={handlePayPalApprove}
                        onError={(err) => {
                          console.error("PayPal error:", err);
                          toast.error(t("Payment failed. Please try again."));
                        }}
                      />
                    </PayPalScriptProvider>
                  )}
                  {showPayPal && !paypalClientId && (
                    <p className="text-sm text-red-600">PayPal is temporarily unavailable. Please try again in a few minutes.</p>
                  )}
                  </div>
                </div>
              </div>
            )}

            {showExtraPackPayPal && selectedPack && (
              <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/50 p-4 sm:p-6">
                <div className="min-h-full flex items-start justify-center py-4 sm:py-8">
                  <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold">Buy +{selectedPack.videos} videos</h3>
                    <button onClick={() => { setShowExtraPackPayPal(false); setSelectedPack(null); }} className="p-2 hover:bg-slate-100 rounded-full">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="mb-6 p-4 bg-slate-50 rounded-lg flex justify-between items-center">
                    <span className="font-medium">Extra videos pack</span>
                    <span className="font-semibold">{formatEuro((selectedPack.price * (extraPackVatApplies ? 1.21 : 1)).toFixed(2))}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-6">
                    {extraPackVatApplies ? t("21% VAT applied.") : t("Valid VAT number detected. VAT not applied.")}
                  </p>

                  {paypalClientId ? (
                    <PayPalScriptProvider options={extraPackPayPalOptions}>
                      <PayPalButtons
                        style={{ layout: "vertical", label: "pay" }}
                        createOrder={handlePayPalCreateExtraOrder}
                        onApprove={handlePayPalApproveExtraOrder}
                        onError={(err) => {
                          console.error("PayPal error:", err);
                          toast.error(t("Payment failed. Please try again."));
                        }}
                      />
                    </PayPalScriptProvider>
                  ) : (
                    <p className="text-sm text-red-600">PayPal is temporarily unavailable. Please try again in a few minutes.</p>
                  )}
                  </div>
                </div>
              </div>
            )}

            {/* FAQ Section */}
            <div className="mt-24">
              <h2 className="text-3xl font-bold text-center mb-12">Frequently asked questions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h4 className="font-semibold mb-2">Can I change plans later?</h4>
                  <p className="text-slate-600 text-sm">
                    Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h4 className="font-semibold mb-2">What payment methods do you accept?</h4>
                  <p className="text-slate-600 text-sm">
                    Payments are made through PayPal for secure payments but you don't have to create an account and can just use your Debit/credit card (Visa, Mastecard...).
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h4 className="font-semibold mb-2">Do unused exports roll over?</h4>
                  <p className="text-slate-600 text-sm">
                    No, exports reset at the beginning of each billing cycle.
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h4 className="font-semibold mb-2">Can I cancel anytime?</h4>
                  <p className="text-slate-600 text-sm">
                    Yes, you can cancel your subscription at any time with no cancellation fees. Cancellation will take effect at the end of your cycle and your subscription will not renew.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="py-12 px-4 md:px-8 bg-slate-900 text-slate-400">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-2">
                <img
                  src="/assets/logo-creavideos.png"
                  alt="CreaVideos"
                  className="h-8 w-auto opacity-80"
                />
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <LocalizedLink to="/templates" className="hover:text-white transition-colors">Templates</LocalizedLink>
                <LocalizedLink to="/" className="hover:text-white transition-colors">Homepage</LocalizedLink>
                <LocalizedLink to="/terms-of-service" className="hover:text-white transition-colors">Terms</LocalizedLink>
                <LocalizedLink to="/license-agreement" className="hover:text-white transition-colors">License Agreement</LocalizedLink>
                <LocalizedLink to="/privacy-policy" className="hover:text-white transition-colors">Privacy</LocalizedLink>
                <LocalizedLink to="/privacy-policy#data-deletion-instructions" className="hover:text-white transition-colors">Data Deletion</LocalizedLink>
                <a href="mailto:support@creavideos.online" className="hover:text-white transition-colors">Support</a>
                <div className="relative">
                  <button
                    type="button"
                    className="hover:text-white transition-colors"
                    onClick={() => setLanguageMenuOpen((prev) => !prev)}
                  >
                    Language
                  </button>
                  {languageMenuOpen && (
                    <div className="absolute right-0 bottom-full mb-2 min-w-36 rounded-lg border border-slate-700 bg-slate-950/95 p-2 shadow-lg">
                      {SUPPORTED_LANGUAGES.map((languageCode) => (
                        <button
                          key={languageCode}
                          type="button"
                          className={`block w-full rounded px-3 py-2 text-left text-sm transition-colors ${
                            currentLanguage === languageCode
                              ? "bg-slate-800 text-white"
                              : "text-slate-300 hover:bg-slate-800 hover:text-white"
                          }`}
                          onClick={() => switchLanguage(languageCode)}
                        >
                          {LANGUAGE_NAMES[languageCode]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm">
              © 2025 CreaVideos. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
  );

  if (!paypalClientId) {
    return pricingContent;
  }

  return pricingContent;
}
